import React, {ReactNode, useEffect, useRef, useState} from "react";
import {Box, Button, Collapse, Link, Skeleton, Stack, Typography} from "@mui/material";
import {Link as RouterLink} from "react-router-dom";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {btnSx, EmptyState, Page, PageHeader, Panel, relative, SkeletonRows} from "../kit";
import {Prose} from "./prose";
import {useWikiPage} from "./use_wiki_page";
import {wikiAdminEditPath} from "./sections";
import {useIsAdmin} from "../utils/wiki_page";
import {dateToString} from "../utils/dates";

interface Heading {
	id: string;
	text: string;
	level: number;
}

/** Índice de la página con la sección visible resaltada. */
const Contents = ({headings, active}: { headings: Heading[], active?: string }) => (
	<Box component="nav" aria-label="En esta página">
		<Stack spacing={0.25} component="ol" sx={{listStyle: "none", p: 0, m: 0}}>
			{headings.map(h => (
				<li key={h.id}>
					<Link href={`#${h.id}`} underline="none"
						  onClick={e => {
							  e.preventDefault();
							  document.getElementById(h.id)?.scrollIntoView({behavior: "smooth", block: "start"});
							  history.replaceState(null, "", `#${h.id}`);
						  }}
						  sx={{
							  display: "block", py: 0.45, pl: h.level === 3 ? 2.5 : 1.25, pr: 1, fontSize: 13, lineHeight: 1.35,
							  borderLeft: "2px solid", borderRadius: "0 6px 6px 0",
							  borderColor: active === h.id ? "primary.main" : "divider",
							  color: active === h.id ? "primary.main" : "text.secondary",
							  fontWeight: active === h.id ? 700 : 500,
							  bgcolor: active === h.id ? "action.hover" : "transparent",
							  "&:hover": {color: "text.primary"},
						  }}>
						{h.text}
					</Link>
				</li>
			))}
		</Stack>
	</Box>
);

const linkLabel = (url: string) => {
	try {
		const u = new URL(url);
		return (u.hostname.replace(/^www\./, "") + u.pathname).replace(/\/$/, "");
	} catch {
		return url;
	}
};

interface WikiDocumentProps {
	code: string;
	section: string;
	/** Título fijo de la pantalla; si falta, el de la página. */
	title?: ReactNode;
	subtitle?: ReactNode;
}

/** Una página de la wiki como documento: cabecera, contenido legible, índice y recursos. */
export const WikiDocument = ({code, section, title, subtitle}: WikiDocumentProps) => {
	const isAdmin = useIsAdmin();
	const {page, loading, notFound} = useWikiPage(code, section);
	const proseRef = useRef<HTMLDivElement>(null);
	const [headings, setHeadings] = useState<Heading[]>([]);
	const [active, setActive] = useState<string>();
	const [contentsOpen, setContentsOpen] = useState(false);

	useEffect(() => {
		const root = proseRef.current;
		if (!root) {
			setHeadings([]);
			return;
		}
		const found = Array.from(root.querySelectorAll<HTMLElement>("h2[id], h3[id]"))
			.map(el => ({id: el.id, text: el.textContent || "", level: el.tagName === "H3" ? 3 : 2}));
		setHeadings(found);
		if (!found.length) return;
		const observer = new IntersectionObserver(entries => {
			const visible = entries.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
			if (visible.length) setActive(visible[0].target.id);
		}, {rootMargin: "-90px 0px -65% 0px"});
		found.forEach(h => {
			const el = document.getElementById(h.id);
			if (el) observer.observe(el);
		});
		return () => observer.disconnect();
	}, [page?.content]);

	const links = [page?.linkA, page?.linkB, page?.linkC].filter((l): l is string => !!l && l.trim() !== "");
	const updated = page?.modified || page?.created;
	const hasAside = headings.length > 1 || links.length > 0;

	const header = (
		<PageHeader
			title={title ?? page?.title ?? (loading ? <Skeleton width={280}/> : "Wiki")}
			subtitle={subtitle ?? (updated ? `Actualizada el ${dateToString(updated)} · ${relative(updated)}` : undefined)}
			actions={isAdmin && page?.id ? (
				<Button variant="outlined" component={RouterLink} to={wikiAdminEditPath(section, page.id)}
						startIcon={<EditRoundedIcon/>} sx={btnSx}>Editar página</Button>
			) : undefined}
		/>
	);

	if (loading) {
		return (
			<Page>
				{header}
				<Panel sx={{p: {xs: 2.5, sm: 4}, maxWidth: 820}}><SkeletonRows rows={8} height={20}/></Panel>
			</Page>
		);
	}

	if (notFound || !page) {
		return (
			<Page>
				{header}
				<Panel>
					<EmptyState icon={<ArticleOutlinedIcon/>} title="No encontramos esta página"
								description="Puede que se haya retirado o que aún no esté publicada. Si llegaste por un enlace, avisa a RR. HH."/>
				</Panel>
			</Page>
		);
	}

	return (
		<Page>
			{header}
			<Box sx={{
				display: "grid", gap: 3, alignItems: "start",
				gridTemplateColumns: {xs: "minmax(0, 1fr)", md: hasAside ? "minmax(0, 1fr) 248px" : "minmax(0, 820px)"},
			}}>
				<Panel sx={{p: {xs: 2.5, sm: 4}, minWidth: 0}}>
					{page.content?.trim()
						? <Box sx={{maxWidth: 760}}><Prose ref={proseRef} content={page.content}/></Box>
						: <EmptyState icon={<ArticleOutlinedIcon/>} title="Esta página aún no tiene contenido"/>}
				</Panel>

				{hasAside && (
					<Stack spacing={2} sx={{position: {md: "sticky"}, top: {md: 88}, order: {xs: -1, md: 0}, minWidth: 0}}>
						{headings.length > 1 && (
							<Panel sx={{p: 2}}>
								<Button fullWidth onClick={() => setContentsOpen(o => !o)}
										endIcon={<ExpandMoreRoundedIcon sx={{transform: contentsOpen ? "rotate(180deg)" : "none", transition: "transform .2s"}}/>}
										sx={{...btnSx, display: {md: "none"}, justifyContent: "space-between", color: "text.primary"}}>
									En esta página · {headings.length}
								</Button>
								<Typography variant="overline" color="text.secondary"
											sx={{display: {xs: "none", md: "block"}, letterSpacing: "0.06em", lineHeight: 1, mb: 1.5}}>
									En esta página
								</Typography>
								<Box sx={{display: {xs: "none", md: "block"}}}><Contents headings={headings} active={active}/></Box>
								<Collapse in={contentsOpen} sx={{display: {md: "none"}}}>
									<Box sx={{pt: 1}} onClick={() => setContentsOpen(false)}><Contents headings={headings} active={active}/></Box>
								</Collapse>
							</Panel>
						)}
						{links.length > 0 && (
							<Panel title="Recursos" sx={{p: 2}}>
								<Stack spacing={1}>
									{links.map(l => (
										<Link key={l} href={l} target="_blank" rel="noopener noreferrer" underline="hover"
											  sx={{display: "flex", gap: 1, alignItems: "flex-start", fontSize: 13, fontWeight: 600, overflowWrap: "anywhere"}}>
											<LinkRoundedIcon fontSize="small" sx={{mt: 0.15, color: "text.secondary"}}/>{linkLabel(l)}
										</Link>
									))}
								</Stack>
							</Panel>
						)}
					</Stack>
				)}
			</Box>
		</Page>
	);
};
